import React, { useEffect, useState } from 'react';
import { TextInput, Button, Spinner } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { userErrorMessage } from '../../../lib/userErrorMessage';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';

// «شناسه سیام تأمین اجتماعی» — the doctor's own id at ONE clinic with تأمین.
//
// It is per doctor per clinic (a doctor in two clinics has two), so it sits under
// each clinic in Settings → «مطب‌ها» and is saved on that membership. The clinic
// can record the same field from its «پرسنل» screen; whichever writes last wins.
// Read only by تأمین e-prescribing, so an empty field is normal.

const MY_CLINIC_SIAM_IDS = `query MyClinicSiamIds { myClinics { id clinicId siamId } }`;
const SET_MY_CLINIC_SIAM = `mutation SetMyClinicSiam($clinicId: ID!, $siamId: String!) {
  setMyClinicSiamId(clinicId: $clinicId, siamId: $siamId) { id clinicId siamId } }`;

/**
 * The doctor's memberships with their SIAM ids, keyed by MEMBERSHIP id (always
 * present, unlike the clinic's details) → { clinicId, siamId }. A query of its own
 * rather than a field on the shared «مطب‌ها» list: if it fails, the list still
 * renders and only the SIAM fields are missing.
 */
export function useClinicSiamIds({ enabled = true } = {}) {
  const [byClinic, setByClinic] = useState({});
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    bffQuery(MY_CLINIC_SIAM_IDS)
      .then((d) => {
        if (!alive) return;
        const map = {};
        for (const m of d?.myClinics || []) map[m.id] = { clinicId: m.clinicId, siamId: m.siamId || '' };
        setByClinic(map);
      })
      .catch(() => { if (alive) setByClinic(null); });
    return () => { alive = false; };
  }, [enabled]);
  return byClinic;
}

export default function ClinicSiamField({ clinicId, value = '' }) {
  const { t, has } = useTranslation();
  const [draft, setDraft] = useState(value || '');
  const [stored, setStored] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft(value || ''); setStored(value || ''); }, [value]);

  const clean = draft.trim();
  const dirty = clean !== stored;

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      const d = await bffMutation(SET_MY_CLINIC_SIAM, { clinicId, siamId: clean });
      const next = d?.setMyClinicSiamId?.siamId || '';
      setStored(next); setDraft(next); setSaved(true);
    } catch (e) {
      // The BFF's own refusal («no membership here») is the useful answer; raw
      // transport text is not (lib/userErrorMessage).
      setError(userErrorMessage(e, { t, has }));
    } finally {
      setBusy(false);
    }
  };

  const id = `siam-${clinicId}`;
  return (
    <div data-testid="settings-clinic-siam" style={{ marginTop: 10 }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: C.textSecondary, marginBottom: 4 }}>
        {t('settings.clinics.siamLabel')}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <TextInput
          id={id}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          disabled={busy}
          dir="ltr"
          autoComplete="off"
          spellCheck={false}
          maxLength={64}
          aria-describedby={`${id}-hint`}
          data-testid="settings-clinic-siam-input"
          style={{ flex: 1, minWidth: 0 }}
        />
        <Button
          variant={dirty ? 'default' : 'outline'}
          size="sm"
          onClick={save}
          disabled={!dirty || busy}
          data-testid="settings-clinic-siam-save"
        >
          {busy && <Spinner size={12} color="currentColor" label="" />}
          {t('settings.clinics.siamSave')}
        </Button>
      </div>
      <div id={`${id}-hint`} style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: 4, lineHeight: 1.6 }}>
        {t('settings.clinics.siamHint')}
      </div>
      {saved && <div role="status" style={{ fontSize: '0.6875rem', color: C.green, marginTop: 2 }}>{t('settings.clinics.siamSaved')}</div>}
      {error && <div role="alert" dir="auto" style={{ fontSize: '0.6875rem', color: C.red, marginTop: 2 }}>{error}</div>}
    </div>
  );
}
